/**
 * F-90 "Import script" UI: the confirm dialog, the progress bar, and the
 * drag-drop target. Mounted once per board view by BoardCanvas; the top bar's
 * Import button (BoardScreen) drives the same store, so a file picked from the
 * menu and a file dropped on the canvas follow one path.
 *
 * The two things this component is careful about:
 *  - **Nothing is written before the dialog.** Parsing happens on the file's
 *    text; the user sees the line count and the caveats (a multi-column CSV
 *    loses its other columns) and only then commits.
 *  - **The write does not freeze the tab** — that is `runScriptImport`'s job
 *    (bounded transactions + a yield between them); this component just renders
 *    the progress it reports. The dialog is modal for the run's duration on
 *    purpose: a local edit made mid-import would merge into the import's undo
 *    group (see `BoardSession.beginUndoGroup`).
 */
import { useCallback, useEffect, useRef, type RefObject } from "react";
import { useStore } from "zustand";

import type { BoardSession } from "../../core/session.js";
import { Modal } from "../../ui/Modal.js";
import { ProgressBar } from "../../ui/ProgressBar.js";
import { showToast } from "../../ui/toast.js";
import { dragHasAudio } from "../bgm/stem.js";
import { importPercent } from "./chunk-plan.js";
import { runScriptImport } from "./import-script.js";
import { isScriptFile, parseScript, scriptFormat } from "./script-file.js";
import { importBusy, type ImportUiStore } from "./store.js";
import { importCaveats, lineCount, importDoneMessage, nothingToImportMessage } from "./wording.js";

export interface ImportScriptLayerProps {
  session: BoardSession;
  ui: ImportUiStore;
  /** The canvas element files are dropped on. */
  containerRef: RefObject<HTMLDivElement | null>;
}

/**
 * Read a file and park the parse in the store. Exported so the top bar's
 * picker and the drop handler cannot drift apart.
 */
export async function beginImport(ui: ImportUiStore, file: File): Promise<void> {
  const format = scriptFormat(file.name);
  if (format === undefined) {
    ui.getState().setPhase({
      kind: "error",
      message: `${file.name} is not a script file. Import a .txt (one line per row), .csv or .tsv file.`,
    });
    return;
  }
  ui.getState().setPhase({ kind: "reading", filename: file.name });
  let text: string;
  try {
    text = await file.text();
  } catch {
    ui.getState().setPhase({
      kind: "error",
      message: `${file.name} could not be read. Check the file and try again.`,
    });
    return;
  }
  // `reading` is async: a second file (or a Cancel) may have moved the store
  // on while this one decoded. Last one in wins its own phase, but a stale
  // read must not stomp it.
  if (ui.getState().phase.kind !== "reading") return;
  ui.getState().setPhase({ kind: "confirm", filename: file.name, preview: parseScript(text, format) });
}

/**
 * The F-28 source column's REAL name. F-28 locks it to "Original Script", but a
 * board seeded before the lock may carry another one — the dialog and the toast
 * must name the column the rows will actually land in.
 */
function sourceColumnName(session: BoardSession, sheetId: string | null): string {
  if (sheetId === null) return "Original Script";
  const colId = session.board.sourceColumnId(sheetId);
  if (colId === undefined) return "Original Script";
  return session.board.getColumn(sheetId, colId)?.name || "Original Script";
}

export function ImportScriptLayer({ session, ui, containerRef }: ImportScriptLayerProps) {
  const phase = useStore(ui, (s) => s.phase);
  const sheetId = useStore(session.store, (s) => s.sheetId);
  // Kept in a ref so the drop listener (registered once) always sees the
  // current phase without re-binding on every progress tick.
  const busyRef = useRef(false);
  busyRef.current = importBusy(phase);

  /* ---------------- drag & drop (F-90) ------------------------------- */

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const hasFiles = (event: DragEvent): boolean =>
      event.dataTransfer !== null && [...event.dataTransfer.types].includes("Files");
    // A drag exposes MIME types but NOT file names, and a .tsv often reports no
    // type at all — so "not an image" is the most this can know. The drop
    // decides for real, by extension.
    const dragHasImage = (event: DragEvent): boolean =>
      [...(event.dataTransfer?.items ?? [])].some(
        (item) => item.kind === "file" && item.type.startsWith("image/"),
      );

    const onDragOver = (event: DragEvent) => {
      if (!hasFiles(event) || dragHasImage(event)) return; // ImageDropLayer's drag
      if (dragHasAudio(event)) return; // a BGM slot's drag (features/bgm/BgmSlotLayer)
      // Always preventDefault: without it the browser navigates the tab to the
      // dropped file, destroying the board view (and any unsynced edit).
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect =
          session.store.getState().frozen || busyRef.current ? "none" : "copy";
      }
    };

    const onDrop = (event: DragEvent) => {
      if (!hasFiles(event) || dragHasImage(event)) return;
      event.preventDefault();
      // Only a script is this layer's. Every other file — music off a slot, a
      // video, a PDF — is ImageDropLayer's now (F-135: it becomes a file
      // element), so a drop of one must not raise "not a script file" here.
      const file = [...(event.dataTransfer?.files ?? [])].find((f) => isScriptFile(f.name));
      if (file === undefined) return;
      if (session.store.getState().frozen) {
        showToast("The board is read-only while offline — nothing was imported");
        return;
      }
      if (busyRef.current) {
        showToast("An import is already running — wait for it to finish");
        return;
      }
      void beginImport(ui, file);
    };

    container.addEventListener("dragover", onDragOver);
    container.addEventListener("drop", onDrop);
    return () => {
      container.removeEventListener("dragover", onDragOver);
      container.removeEventListener("drop", onDrop);
    };
  }, [containerRef, session, ui]);

  /* ---------------- the run ------------------------------------------ */

  const start = useCallback(
    async (filename: string, rows: readonly string[]) => {
      if (sheetId === null) return;
      // F-28: the target is always the locked Original Script column, resolved
      // at click time — a board seeded before F-28 has none, and there is
      // nothing this feature can do about that but say so.
      const colId = session.board.sourceColumnId(sheetId);
      if (colId === undefined) {
        ui.getState().setPhase({
          kind: "error",
          message: 'This board has no "Original Script" column to import into.',
        });
        return;
      }
      const columnName = sourceColumnName(session, sheetId);
      ui.getState().setPhase({ kind: "running", filename, done: 0, total: rows.length });
      try {
        const result = await runScriptImport({
          session,
          sheetId,
          colId,
          rows,
          onProgress: (done, total) => {
            // Ignore ticks from a run the user has already moved past (the
            // board view unmounting resets the store).
            if (ui.getState().phase.kind !== "running") return;
            ui.getState().setPhase({ kind: "running", filename, done, total });
          },
        });
        showToast(importDoneMessage(result, rows.length, columnName));
      } catch (error) {
        showToast(
          error instanceof Error
            ? `Import failed: ${error.message}`
            : "Import failed — nothing else was written.",
        );
      } finally {
        ui.getState().reset();
      }
    },
    [session, sheetId, ui],
  );

  if (phase.kind === "idle") return null;

  if (phase.kind === "error") {
    return (
      <Modal title="Import script" onClose={() => ui.getState().reset()}>
        <p className="import-error" role="alert">
          {phase.message}
        </p>
        <div className="modal-actions">
          <button type="button" className="btn btn-primary" onClick={() => ui.getState().reset()}>
            Close
          </button>
        </div>
      </Modal>
    );
  }

  if (phase.kind === "reading") {
    return (
      <Modal title="Import script" onClose={() => ui.getState().reset()}>
        <p className="import-note">Reading {phase.filename}…</p>
      </Modal>
    );
  }

  if (phase.kind === "running") {
    const percent = importPercent(phase.done, phase.total);
    return (
      // No onClose: the run holds an undo group open, and dismissing the
      // dialog would not stop it — a bar that cannot be closed is honest here.
      <Modal title="Importing script" onClose={() => {}}>
        <p className="import-note">
          Writing {lineCount(phase.total)} from {phase.filename}…
        </p>
        <ProgressBar percent={percent} label={`${phase.done} of ${phase.total} rows`} />
      </Modal>
    );
  }

  // F-16 large paste — the same operation as an import, so the same bar. The
  // modal is not decoration: the paste holds an undo group open for its whole
  // duration (captureTimeout at Infinity), so any edit made while it runs would
  // merge into the paste's single undo item and be destroyed by the Ctrl+Z that
  // reverts the paste. Blocking input for the ~10-30 s it takes is what stops
  // that, exactly as for the import above.
  if (phase.kind === "pasting") {
    const percent = importPercent(phase.done, phase.total);
    return (
      <Modal title="Pasting rows" onClose={() => {}}>
        <p className="import-note">Writing {lineCount(phase.total)} from the clipboard…</p>
        <ProgressBar percent={percent} label={`${phase.done} of ${phase.total} rows`} />
      </Modal>
    );
  }

  const { preview, filename } = phase;
  const caveats = importCaveats(preview);
  const empty = preview.rows.length === 0;
  const targetName = sourceColumnName(session, sheetId);

  return (
    <Modal title="Import script" onClose={() => ui.getState().reset()}>
      <div className="import-dialog">
        {empty ? (
          <p className="import-note">{nothingToImportMessage(filename)}</p>
        ) : (
          <p className="import-note">
            <strong>{filename}</strong> — {lineCount(preview.rows.length)} into the{" "}
            <strong>{targetName}</strong> column, one line per row.
          </p>
        )}
        {caveats.length > 0 && (
          <ul className="import-caveats">
            {caveats.map((caveat) => (
              <li key={caveat}>{caveat}</li>
            ))}
          </ul>
        )}
        {!empty && <ImportSample rows={preview.rows} />}
        <div className="modal-actions">
          <button type="button" className="btn" onClick={() => ui.getState().reset()}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={empty || sheetId === null}
            onClick={() => void start(filename, preview.rows)}
          >
            Import
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * The first few lines, verbatim. F-60's split dialog established that a preview
 * is what makes a bulk row operation trustworthy; here it is also the only way
 * to catch a wrong-column CSV or a mis-detected delimiter BEFORE 10,000 rows
 * land in the doc.
 */
function ImportSample({ rows }: { rows: readonly string[] }) {
  const sample = rows.slice(0, 3);
  return (
    <div className="import-sample" aria-label="First lines">
      {sample.map((row, i) => (
        <div key={i} className="import-sample-row" title={row}>
          {row}
        </div>
      ))}
      {rows.length > sample.length && (
        <div className="import-sample-more">+{rows.length - sample.length} more</div>
      )}
    </div>
  );
}
